// Page chrome shared by every route: banners, language switcher, header, footer, page head, breadcrumbs, related links.
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import Mascot from './Mascot'
import { Claims } from './Cite'
import { CONTENT_UPDATED, LAST_VERIFIED, urls, type Claim } from './data'
import { T, useT } from './i18n'
import { useAlt } from './i18n/alt'
import { LOCALES, localizePath, type LocaleCode } from './i18n/locales'
import { routeFor, routes, to } from './routes'
import { Accent, MONO, LINE, Sym, eyebrow, ext, h1Style, para, wrap, wrapPad } from './ui'

export const LANG_COOKIE = 'inh_lang'

/** Visible on every page, labelled with a globe so people notice it. Lists only languages this page is built in
 *  (drafts included). Preserves the current page. */
export function LanguageSwitcher({ path, id }: { path: string; id?: string }) {
  const { t, locale } = useT()
  const alt = useAlt()[path] ?? {}
  const available = LOCALES.filter(l => l.code === 'en' || alt[l.code])
  if (available.length < 2) return null
  const remember = (code: LocaleCode) => { try { document.cookie = `${LANG_COOKIE}=${code}; Path=/; Max-Age=31536000; SameSite=Lax` } catch { /* blocked */ } }
  const labelId = (id ?? 'lang') + '-label'
  return (
    <nav aria-labelledby={labelId} className="lang-switch">
      <span id={labelId} className="lang-switch-label"><Sym n="language" style={{ fontSize: 18 }} />{t('i18n.switcher')}</span>
      <ul>
        {available.map(l => (
          <li key={l.code}>
            <a href={localizePath(path, l.code)} hrefLang={l.hreflang} lang={l.htmlLang} aria-current={l.code === locale ? 'true' : undefined} onClick={() => remember(l.code)} className="lang-opt">
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}

/** Slim, plain disclaimer above the header on every page, with the language switcher. Not styled like a government banner. */
export function IndependenceBanner({ path }: { path: string }) {
  const { t, L } = useT()
  return (
    <div className="indep-banner" role="note" aria-label={t('banner.aria')}>
      <span><T k="banner.text" components={{ b: <strong />, iras: <a href={urls.irasQueries} {...ext} />, imda: <a href={urls.imdaContact} {...ext} /> }} /></span>
      <span style={{ display: 'flex', gap: '6px 20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <LanguageSwitcher path={path} />
        <a href={L('/about')} className="indep-more">{t('banner.about')}</a>
      </span>
    </div>
  )
}

/** On translated pages: the English version prevails, with a link to it; drafts say so plainly. */
export function TranslationBanner({ path }: { path: string }) {
  const { t, locale } = useT()
  const status = useAlt()[path]?.[locale]
  if (locale === 'en') return null
  return (
    <div className="tr-banner" role="note">
      <span><T k="i18n.unofficial" components={{ b: <strong /> }} /> {status !== 'reviewed' && <strong className="tr-draft">{t('i18n.draft')}</strong>}</span>
      <a href={path} hrefLang="en" lang="en" onClick={() => { try { document.cookie = `${LANG_COOKIE}=en; Path=/; Max-Age=31536000; SameSite=Lax` } catch { /* blocked */ } }}>{t('i18n.englishLink')}</a>
    </div>
  )
}

export function Header({ current }: { current: string }) {
  const { t, L } = useT()
  const [open, setOpen] = useState(false)
  // While the mobile menu is open: lock page scroll, close on Escape or when the screen grows past the breakpoint.
  useEffect(() => {
    if (!open) return
    const root = document.documentElement
    root.classList.add('menu-open')
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const lang = document.documentElement.lang
    const mq = window.matchMedia(lang === 'ta' || lang === 'ms' ? '(min-width: 1501px)' : '(min-width: 1241px)')
    const onMq = () => { if (mq.matches) setOpen(false) }
    window.addEventListener('keydown', onKey); mq.addEventListener('change', onMq)
    return () => { root.classList.remove('menu-open'); window.removeEventListener('keydown', onKey); mq.removeEventListener('change', onMq) }
  }, [open])
  const close = () => setOpen(false)
  const navLinks = routes.filter(r => r.nav).map(r => [r.path, t(`meta.${r.slug}.nav`)] as const)
  const mobileLinks = [['/', t('nav.overview')] as const, ...routes.filter(r => r.path !== '/').map(r => [r.path, t(`meta.${r.slug}.crumb`)] as const)]
  return (
    <header className="site-header" style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(5,38,32,0.94)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(178,245,220,0.12)' }}>
      <div className="hdr-row" style={{ ...wrap, padding: '0 var(--gx)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}>
        <a href={L('/')} onClick={close} lang="en" style={{ display: 'flex', alignItems: 'center', gap: 14, color: '#b2f5dc', textDecoration: 'none' }}>
          <span className="hdr-mascot" style={{ display: 'block' }}><Mascot /></span>
          <span className="hdr-name" style={{ display: 'flex', flexDirection: 'column', fontWeight: 500, lineHeight: 1.02, letterSpacing: '-0.01em' }}><span>InvoiceNow</span><span>Hub</span></span>
        </a>
        <nav className="nav-desk" aria-label={t('nav.main')} style={{ alignItems: 'center', gap: 28, fontSize: 15 }}>
          {navLinks.map(([h, l]) => <a key={h} href={L(h)} className="nav-link" aria-current={h === current ? 'page' : undefined} style={{ color: h === current ? '#b2f5dc' : '#f2f6f4' }}>{l}</a>)}
          <a href={L(to('what-to-do'))} className="btn-fill" style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#b2f5dc', color: '#052620', padding: '13px 20px', fontWeight: 500, textDecoration: 'none' }}>{t('nav.whatToDo')} <span style={{ fontFamily: MONO }}>→</span></a>
          <a href={L(to('question'))} className="btn-line" style={{ border: '1px solid #b2f5dc', color: '#f2f6f4', padding: '12px 18px', fontWeight: 500, textDecoration: 'none' }}>{t('nav.question')}</a>
        </nav>
        <div className="nav-mob-bar" style={{ alignItems: 'center', gap: 8 }}>
          <a href={L(to('what-to-do'))} onClick={close} className="btn-fill nav-mob-cta" style={{ background: '#b2f5dc', color: '#052620', padding: '10px 14px', fontSize: 14, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>{t('nav.whatToDoShort')} <span style={{ fontFamily: MONO }}>→</span></a>
          <button className="menu-btn" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? t('nav.closeMenu') : t('nav.openMenu')}>
            <Sym n={open ? 'close' : 'menu'} />
          </button>
        </div>
      </div>
      <nav id="mobile-menu" className={'nav-mob' + (open ? ' open' : '')} aria-label={t('nav.main')} hidden={!open}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {mobileLinks.map(([h, l], i) => (
            <a key={h} href={L(h)} onClick={close} aria-current={h === current ? 'page' : undefined} className="nav-mob-link" style={{ '--i': i } as CSSProperties}>
              <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.1em', color: '#9dbdb2' }}>{String(i + 1).padStart(2, '0')}</span>{l}
            </a>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 28 }}>
          <a href={L(to('what-to-do'))} onClick={close} className="btn-fill" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#b2f5dc', color: '#052620', padding: '16px 20px', fontSize: 17, fontWeight: 600, textDecoration: 'none' }}>{t('nav.whatToDo')} <span style={{ fontFamily: MONO }}>→</span></a>
          <a href={L(to('question'))} onClick={close} className="btn-line" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #b2f5dc', color: '#f2f6f4', padding: '15px 20px', fontSize: 17, fontWeight: 500, textDecoration: 'none' }}>{t('nav.question')} <span style={{ fontFamily: MONO }}>→</span></a>
        </div>
        <div style={{ marginTop: 28 }}><LanguageSwitcher path={current} id="lang-mob" /></div>
      </nav>
    </header>
  )
}

export function Footer() {
  const { t, L, date } = useT()
  const links = [['/', t('nav.overview')] as const, ...routes.filter(r => r.path !== '/').map(r => [r.path, t(`meta.${r.slug}.crumb`)] as const)]
  return (
    <footer style={{ ...wrap, padding: 'calc(var(--py) * .8) var(--gx) 104px', display: 'flex', flexDirection: 'column', gap: 64 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', gap: 40, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'end', gap: 24 }} lang="en">
          <span style={{ display: 'block', width: 88, height: 96 }}><Mascot /></span>
          <span style={{ fontSize: 'clamp(40px,5vw,72px)', fontWeight: 500, letterSpacing: '-0.04em', lineHeight: 0.95, color: '#b2f5dc' }}>InvoiceNow<br />Hub</span>
        </div>
        <nav aria-label={t('nav.footer')} style={{ display: 'flex', gap: '12px 28px', flexWrap: 'wrap', fontSize: 15 }}>
          {links.map(([h, l]) => <a key={h} href={L(h)} style={{ color: '#f2f6f4' }}>{l}</a>)}
          <a href={L('/about#privacy')} style={{ color: '#f2f6f4' }}>{t('nav.privacy')}</a>
        </nav>
      </div>
      <p style={{ margin: 0, paddingTop: 24, borderTop: `1px solid ${LINE}`, fontSize: 13, lineHeight: 1.6, color: '#9dbdb2', maxWidth: 860 }}>
        <T k="footer.disclaimer" values={{ date: date(LAST_VERIFIED) }} components={{ iras: <a href={urls.irasQueries} {...ext} />, imda: <a href={urls.imdaContact} {...ext} /> }} />
      </p>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', ...eyebrow('#9dbdb2') }}>
        <span><T k="footer.made" components={{ heart: <span className="heart" role="img" aria-label={t('footer.love')} /> }} /></span>
        <span>{t('footer.updated', { date: date(CONTENT_UPDATED).toLocaleUpperCase() })}</span>
      </div>
    </footer>
  )
}

/** Visible breadcrumb trail (the matching BreadcrumbList JSON-LD is added by scripts/prerender.mjs). */
export function Breadcrumbs({ path }: { path: string }) {
  const { t, L } = useT()
  const r = routeFor(path)
  if (!r || r.path === '/') return null
  return (
    <nav aria-label={t('common.breadcrumb')} style={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.06em', color: '#9dbdb2' }}>
      <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <li><a href={L('/')}>{t('common.home')}</a> <span aria-hidden>/</span></li>
        <li aria-current="page">{t(`meta.${r.slug}.crumb`).toLocaleUpperCase()}</li>
      </ol>
    </nav>
  )
}

/** Top of every sub-page: breadcrumbs, eyebrow, the page's only H1 and a one-line intro. */
/** Page title block. `answer`: sourced sentences that answer the page's main question straight away, so the first
 *  thing a reader (or a search or AI engine quoting the page) sees is the answer with its source. */
export function PageHead({ path, slug, answer }: { path: string; slug: string; answer?: Claim[] }) {
  const { t } = useT()
  return (
    <div style={{ ...wrapPad, paddingBottom: 'calc(var(--py) * .6)', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumbs path={path} />
      <span style={eyebrow()}>{t(`head.${slug}.eyebrow`)}</span>
      <h1 style={h1Style}><T k={`head.${slug}.title`} components={{ accent: <Accent /> }} /></h1>
      <p style={para}>{t(`head.${slug}.intro`)}</p>
      {answer && <p style={{ ...para, borderLeft: '2px solid #b2f5dc', paddingLeft: 16 }}><Claims claims={answer} /></p>}
    </div>
  )
}

export function Related({ paths }: { paths: string[] }) {
  const { t, L } = useT()
  return (
    <nav aria-label={t('common.relatedNav')} style={{ borderTop: '1px solid rgba(178,245,220,0.12)' }}>
      <div style={{ ...wrap, padding: '48px var(--gx)', display: 'flex', alignItems: 'center', gap: '12px 16px', flexWrap: 'wrap' }}>
        <span style={eyebrow('#9dbdb2')}>{t('common.related')}</span>
        {paths.map(h => <a key={h} href={L(h)} className="btn-line" style={{ border: '1px solid #b2f5dc', padding: '12px 18px', fontWeight: 500, textDecoration: 'none' }}>{t(`meta.${routeFor(h)!.slug}.crumb`)} →</a>)}
      </div>
    </nav>
  )
}

export const Section = ({ id, children, style }: { id?: string; children: ReactNode; style?: CSSProperties }) => (
  <section id={id} style={style}><div style={wrapPad}>{children}</div></section>
)
